import React, { useState } from "react";
import cx from "classnames";
import TextareaAutosize from "react-autosize-textarea";
import { upperFirst } from "lodash-es";

import styles from "./input.module.scss";

export namespace Input {
  export type Props = Omit<React.HTMLProps<any>, "size"> & {
    alignText?: "left" | "right" | "center";
    block?: boolean;
    condensed?: boolean;
    error?: boolean;
    grow?: boolean;
    inputRef?: React.RefObject<any> | ((ref: any) => void);
    minHeight?: number;
    multiline?: boolean;
    prefix?: string;
    resizable?: boolean;
    shortcut?: JSX.Element;
    size?: "small" | "medium";
    suffix?: string;
    type?: "number" | "password" | "tel" | "text";
    width?: number;
  };
}

export const Input = ({
  alignText = "left",
  className,
  disabled,
  error = false,
  grow = true,
  inputRef,
  minHeight,
  multiline,
  onBlur = (): void => {},
  onFocus = (): void => {},
  prefix,
  resizable,
  shortcut,
  size = "medium",
  suffix,
  type = "text",
  width,
  ...props
}: Input.Props): JSX.Element => {
  const [focused, setFocused] = useState(false);

  const handleFocus = (event: React.FocusEvent<any>): void => {
    onFocus(event);
    setFocused(true);
  };

  const handleBlur = (event: React.FocusEvent<any>): void => {
    onBlur(event);
    setFocused(false);
  };

  const renderInputArea = (): JSX.Element => {
    if (!multiline) {
      return (
        <input
          {...props}
          disabled={disabled}
          className={styles.inputCore}
          onFocus={handleFocus}
          onBlur={handleBlur}
          ref={inputRef}
          type={type}
        />
      );
    }

    if (!grow) {
      return (
        <textarea
          {...props}
          disabled={disabled}
          className={styles.inputCore}
          onFocus={handleFocus}
          onBlur={handleBlur}
          ref={inputRef}
          style={{ minHeight }}
        />
      );
    }

    return (
      <TextareaAutosize
        {...props}
        className={styles.inputCore}
        onFocus={handleFocus}
        onBlur={handleBlur}
        ref={inputRef}
        style={{ minHeight }}
      />
    );
  };

  const classList = cx(
    className,
    styles.input,
    error && styles.inputError,
    focused && styles.inputFocused,
    width && styles.inputInline,
    styles[`input${upperFirst(size)}` as keyof typeof styles],
    styles[`inputAlign${upperFirst(alignText)}` as keyof typeof styles],
    multiline && styles.inputMultiline,
    multiline && resizable && styles.inputResizable,
    disabled && styles.inputDisabled
  );

  return (
    <div
      className={classList}
      style={width ? { width: `${width}px` } : undefined}
    >
      {prefix && (
        <div className={styles.inputPrefixed}>
          <span>{prefix}</span>
        </div>
      )}
      {shortcut && <div className={styles.inputShortcut}>{shortcut}</div>}
      {renderInputArea()}
      {suffix && (
        <div className={styles.inputSuffixed}>
          <span>{suffix}</span>
        </div>
      )}
    </div>
  );
};
